import { configureStore, combineSlices, EnhancedStore } from '@reduxjs/toolkit';
import { isDevelopment } from './constants';
import { baseApiAuthenticated } from '@/api/v2';
import { widgetManifestApi } from '@/api/v2/widgets';
import { authMiddleware } from '@/middleware/authMiddleware';
// RTK Query middleware must be registered at store creation — there is no
// lazy path for it — so the assistant api is wired explicitly.
import { assistantApi } from '@starjack/entities/assistantConfig';
import { registerStore, type LazyLoadedSlicesRegistry } from '@starjack/api';

// Features automatically augment LazyLoadedSlicesRegistry when imported
// No need to import or reference individual slices here!

// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
export interface LazyLoadedSlices {}

export const rootReducer = combineSlices(
    baseApiAuthenticated,
    assistantApi,
    widgetManifestApi
).withLazyLoadedSlices<LazyLoadedSlices>();

// Auto module augmentation - features automatically add themselves to the registry
declare module '@/store' {
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
    export interface LazyLoadedSlices extends LazyLoadedSlicesRegistry {}
}

// Features auto-inject when imported anywhere in the app (via registerSliceInjection)
// No explicit initialization needed - they inject themselves!

// Wrap with clearAuthData logic
const finalReducer = (state, action) => {
    if (action.type === 'authSlice/clearAuthData') {
        return {
            ...rootReducer(undefined, action),
        };
    }

    return rootReducer(state, action);
};

export type Store = NonNullable<Parameters<typeof rootReducer>[0]>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const store: EnhancedStore<Store, any, any> = configureStore({
    reducer: finalReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false,
            immutableCheck: false,
        }).concat(
            authMiddleware,
            baseApiAuthenticated.middleware,
            assistantApi.middleware,
            widgetManifestApi.middleware
        ),

    devTools: isDevelopment,
});

// Register store in global registry for auto-injection
registerStore(rootReducer, baseApiAuthenticated, store);
